import { RestError } from '@azure/core-http';
import {
  CreateProjectDto,
  EditProjectDto,
  ProjectDto,
  SettingsTreeDto,
  UploadImageDto,
  UploadImageResponseDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useMutation, useQueries, useQuery, useQueryClient } from 'react-query';
import { useApi } from '../../core/api/api.context';
import { QueryKeys } from '../../core/queries/query-keys.constants';

export function useQueryGetSettingsTree(projectID: string) {
  const api = useApi();

  return useQuery<SettingsTreeDto, RestError>([QueryKeys.SETTINGS, projectID], async () => {
    return await api.settingsController.getProjectTree(projectID);
  });
}

export function useQueriesGetSettingsTreeProjectChildren(projectIDs: string[]) {
  const api = useApi();
  return useQueries(
    projectIDs.map((projectID) => ({
      queryKey: [QueryKeys.SETTINGS, projectID, QueryKeys.SETTINGS_CHILDREN],
      queryFn: async () => {
        return await api.settingsController.getNodesUnderProject(projectID);
      }
    }))
  );
}

export function useMutationUpdateProject() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    ProjectDto,
    RestError,
    {
      projectID: string;
      dto: EditProjectDto;
    }
  >(
    async ({ dto, projectID }: { projectID: string; dto: EditProjectDto }) => {
      const result = await api.projectsController.edit(projectID, dto);
      // The API doesn't throw an error on 4xx or 5xx, instead it returns an object with a body that contains the error message
      // This is a workaround to throw an error in the mutation
      const unknownResult = result as any;
      if ('body' in unknownResult && 'statusCode' in unknownResult.body && 'message' in unknownResult.body) {
        throw new RestError(
          (unknownResult as any).body.message as string,
          undefined,
          (unknownResult as any).body.statusCode as number
        );
      }
      return result;
    },
    {
      onSuccess(_, { projectID }) {
        queryClient.invalidateQueries([QueryKeys.PROJECTS, projectID]);
      }
    }
  );
}

export function useMutationCreateProject() {
  const api = useApi();

  return useMutation<
    ProjectDto,
    RestError,
    {
      dto: CreateProjectDto;
    }
  >(async ({ dto }: { dto: CreateProjectDto }) => {
    const result = await api.projectsController.create(dto);
    // The API doesn't throw an error on 4xx or 5xx, instead it returns an object with a body that contains the error message
    // This is a workaround to throw an error in the mutation
    const unknownResult = result as any;
    if ('body' in unknownResult && 'statusCode' in unknownResult.body && 'message' in unknownResult.body) {
      throw new RestError(
        (unknownResult as any).body.message as string,
        undefined,
        (unknownResult as any).body.statusCode as number
      );
    }
    return result;
  });
}

export function useMutationDeleteProject() {
  const api = useApi();

  return useMutation<
    void,
    RestError,
    {
      projectID: string;
    }
  >(async ({ projectID }: { projectID: string }) => {
    await api.projectsController.delete(projectID);
  });
}

export function useMutationUploadImage() {
  const api = useApi();

  return useMutation<UploadImageResponseDto, RestError, UploadImageDto>(async (dto: UploadImageDto) => {
    return await api.uploadController.uploadImage(dto);
  });
}
